import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, defaultIfEmpty, retry, switchMap, take, timeout } from 'rxjs/operators';
import { LegalConsentService } from '../api/legal-consent.service';
import { PlatformTermsReacceptanceComponent } from '../../../pages/login/platform-terms-reacceptance/platform-terms-reacceptance.component';
import { PostLoginInterruptService } from './post-login-interrupt.service';
import { INTERRUPT_PRIORITY, PLATFORM_TERMS_REACCEPTANCE_INTERRUPT_KEY } from './post-login-interrupt.model';

/**
 * Bounds each attempt of the status check and of the translate lookup that follows it, so a
 * request that HANGS (as opposed to erroring) still settles. With one retry that is up to
 * 2 x 800ms for the status call plus 800ms for the lookup (~2.4s), which is close to — not
 * comfortably inside — the interrupt collection round's 2.5s window. If a slow check loses
 * that race the round is released without the gate, and the next navigation off the review
 * route is the only thing that re-raises it; keep that in mind before lengthening anything.
 */
export const PLATFORM_TERMS_CHECK_TIMEOUT_MS = 800;

const REVIEW_ROUTE = '/website-terms-conditions';

/**
 * The platform T&C re-acceptance gate.
 *
 * Asks the server whether the signed-in user is current on the newest material PlatformTerms
 * version and, if not (or if that cannot be determined), queues the mandatory re-acceptance
 * interrupt. Applies to every user, not just P2P users, because the withdrawal-limits clause
 * reaches anyone with a wallet.
 *
 * It fails CLOSED. Only an explicit `needsReacceptance === false` lets a user through; an
 * error, a timeout, an empty completion (the access-token interceptor returns EMPTY when
 * authentication disappears mid-request) and a malformed body all queue the gate. When the
 * version could not be determined the modal shows a retry state rather than something to
 * accept, so the user is never recorded as accepting text they were not shown.
 *
 * Callers pass an `isCurrent` guard bound to the session that asked. Every async callback
 * checks it before touching the interrupt queue, so a response that lands after logout, after
 * login-on-behalf swapped the token, or after a same-user re-login cannot act on the new
 * session's collection round.
 */
@Injectable({ providedIn: 'root' })
export class PlatformTermsGateService {
  /**
   * Set while the review-route exemption is the only reason nothing was queued. The review link
   * in the modal opens the terms page in a new tab, which boots a fresh app against the same
   * session; raising the blocking gate there would cover the page the user came to read.
   */
  private exemptSession: (() => boolean) | null = null;

  constructor(
    private readonly legalConsentService: LegalConsentService,
    private readonly interruptService: PostLoginInterruptService,
    private readonly translate: TranslateService,
    private readonly router: Router,
  ) {}

  /**
   * Run the check for the session `isCurrent` is bound to. Always reports the gate's source to
   * the interrupt collection round, whatever the outcome, so the round never waits on it.
   */
  check(isCurrent: () => boolean): void {
    if (this.exemptWhileReadingTerms(isCurrent)) {
      return;
    }

    this.legalConsentService
      .getPlatformTermsReacceptanceStatus()
      .pipe(
        take(1),
        timeout(PLATFORM_TERMS_CHECK_TIMEOUT_MS),
        retry(1),
        defaultIfEmpty(null),
        catchError(() => of(null)),
      )
      .subscribe((status) => {
        if (!isCurrent() || this.exemptWhileReadingTerms(isCurrent)) {
          return;
        }
        // An explicit `false`, not merely falsy: a null or malformed body must not read as
        // "compliant".
        if (status?.needsReacceptance === false) {
          this.report();
          return;
        }
        this.enqueue(status?.currentVersion ?? null, status?.currentVersionId ?? null, isCurrent);
      });
  }

  /**
   * Call on every navigation. Re-checks as soon as the user leaves the terms page, so the
   * review-route exemption cannot outlive the page it exists for.
   */
  onNavigation(): void {
    const session = this.exemptSession;
    if (!session || this.isOnReviewRoute()) {
      return;
    }
    this.exemptSession = null;
    if (session()) {
      this.check(session);
    }
  }

  /** Forget any pending exemption — the session it belonged to has ended. */
  reset(): void {
    this.exemptSession = null;
  }

  private exemptWhileReadingTerms(isCurrent: () => boolean): boolean {
    if (!this.isOnReviewRoute()) {
      return false;
    }
    this.exemptSession = isCurrent;
    this.report();
    return true;
  }

  private isOnReviewRoute(): boolean {
    return this.router.url.split(/[?#]/)[0].startsWith(REVIEW_ROUTE);
  }

  private report(): void {
    this.interruptService.sourceReported(PLATFORM_TERMS_REACCEPTANCE_INTERRUPT_KEY);
  }

  private enqueue(version: string | null, versionId: number | null, isCurrent: () => boolean): void {
    const labelKey = 'postLoginInterrupts.platformTermsReacceptance.label';
    const summaryKey = 'postLoginInterrupts.platformTermsReacceptance.summary';

    const fallback = () =>
      of({
        [labelKey]: this.translate.instant(labelKey),
        [summaryKey]: this.translate.instant(summaryKey),
      });

    this.translate
      .get([labelKey, summaryKey])
      .pipe(
        take(1),
        timeout(PLATFORM_TERMS_CHECK_TIMEOUT_MS),
        defaultIfEmpty(null),
        switchMap((copy) => (copy === null ? fallback() : of(copy))),
        catchError(fallback),
      )
      .subscribe((copy) => {
        if (!isCurrent() || this.exemptWhileReadingTerms(isCurrent)) {
          return;
        }
        this.interruptService.enqueue({
          key: PLATFORM_TERMS_REACCEPTANCE_INTERRUPT_KEY,
          kind: 'gate',
          // No defer path at all, unlike the (also kind: 'gate') profile-refresh nudge. The frame
          // and its modal options key off this, not `kind`, to disable x / Esc / backdrop.
          mandatory: true,
          priority: INTERRUPT_PRIORITY.complianceGate,
          label: copy[labelKey],
          summary: copy[summaryKey],
          component: PlatformTermsReacceptanceComponent,
          inputs: { version, versionId },
          // Single-interrupt path: no backdrop / Esc dismiss, so accepting is the only way out.
          // Inside the frame, the frame owns the chrome.
          modalOptions: {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            windowClass: 'platform-terms-reacceptance-modal',
            ariaLabelledBy: 'platform-terms-reacceptance-title',
          },
        });
        this.report();
      });
  }
}
